import axios from 'axios';
import type { V1UploadFileResponse } from '@/generated/hooks/bishop/models';
import type { TUploadedFile } from '@/shared/types';

/**
 * Uploads a single file to Bishop API using the workspace-scoped endpoint.
 *
 * @param file - File to upload
 * @param organizationId - Organization ID for the upload
 * @param workspaceId - Workspace ID for the upload
 * @returns Promise resolving to uploaded file metadata
 * @throws {Error} If the response does not contain a resource name
 */
export const uploadFileToBishop = async (
  file: File,
  organizationId: string,
  workspaceId: string,
): Promise<TUploadedFile> => {
  const formData = new FormData();
  formData.append('content_type', file.type);
  formData.append('file', file, file.name);

  const response = await axios.post<V1UploadFileResponse>(
    `/api/v1/organizations/${organizationId}/workspaces/${workspaceId}/files:upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );

  const fileResourceName = response.data.metadata?.name;

  if (!fileResourceName) {
    throw new Error('Failed to upload file: no resource name returned');
  }

  return {
    name: file.name,
    resourceName: fileResourceName,
  };
};

/**
 * Replaces the bytes of an existing Bishop file, keeping its resource name.
 *
 * @param file - New file whose content replaces the stored bytes
 * @param fileId - Id of the file to replace (an asset's id is its file id)
 * @param organizationId - Organization ID owning the file
 * @param workspaceId - Workspace ID owning the file
 */
export const replaceFileContent = async (
  file: File,
  fileId: string,
  organizationId: string,
  workspaceId: string,
): Promise<void> => {
  const formData = new FormData();
  formData.append('content', file, file.name);

  await axios.post(
    `/api/v1/organizations/${organizationId}/workspaces/${workspaceId}/files/${fileId}:replaceContent`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

/**
 * Re-syncs an asset row with its file's current bytes, refreshing `size_bytes`,
 * `mime_type` and `update_time`. Call after replaceFileContent, otherwise the
 * asset keeps reporting the previous file's metadata.
 *
 * @param assetId - Id of the asset to refresh (same id as its file)
 * @param organizationId - Organization ID owning the asset
 * @param workspaceId - Workspace ID owning the asset
 */
export const refreshAssetContent = async (
  assetId: string,
  organizationId: string,
  workspaceId: string,
): Promise<void> => {
  await axios.post(
    `/api/v1/organizations/${organizationId}/workspaces/${workspaceId}/assets/${assetId}:refreshContent`,
  );
};

/**
 * Uploads multiple files to Bishop API in parallel.
 *
 * @param files - Array of files to upload
 * @param organizationId - Organization ID for the upload
 * @param workspaceId - Workspace ID for the upload
 * @returns Promise resolving to array of uploaded file metadata
 */
export const uploadFilesToBishop = async (
  files: File[],
  organizationId: string,
  workspaceId: string,
): Promise<TUploadedFile[]> => {
  if (!files || files.length === 0) return [];

  return Promise.all(
    files.map(file => uploadFileToBishop(file, organizationId, workspaceId)),
  );
};
